const SENTINEL_URL = 'ws://ssm-tunnel.local/';

/**
 * SsmWebSocket — a `WebSocket`-compatible object backed by an {@link SsmTunnel}.
 *
 * ironrdp-web (the browser RDP client) connects by constructing
 * `new WebSocket(proxyAddress)` *inside* the wasm (via web-sys/gloo-net) and
 * speaking RDCleanPath over it. We can't hand it a transport directly, so for
 * "RDP over SSM" we point its proxyAddress at a sentinel URL and swap the global
 * `WebSocket` constructor (see {@link installSsmWebSocket}) so that one URL
 * resolves to this object — which pipes bytes over the SSM tunnel instead of a
 * real socket. Every other URL still gets the native WebSocket.
 *
 * It implements just the surface gloo-net touches: `binaryType`, `readyState`,
 * `send`, `close`, the `on{open,message,close,error}` handlers, and
 * `addEventListener`/`removeEventListener` (via EventTarget). Inbound frames are
 * delivered as `ArrayBuffer` message events (binaryType is always arraybuffer).
 *
 * The WebSocket `open` event fires when the tunnel reports `ready` (the SSM
 * data channel is established and able to carry bytes), which is the point at
 * which the client may start sending RDCleanPath.
 */
export default class SsmWebSocket extends EventTarget {
  static CONNECTING = 0;
  static OPEN = 1;
  static CLOSING = 2;
  static CLOSED = 3;

  constructor(tunnel, url = SENTINEL_URL) {
    super();
    this.url = url;
    this.protocol = '';
    this.binaryType = 'arraybuffer';
    this.bufferedAmount = 0;
    this.readyState = SsmWebSocket.CONNECTING;

    this.onopen = null;
    this.onmessage = null;
    this.onclose = null;
    this.onerror = null;

    this._tunnel = tunnel;
    this._sendQueue = Promise.resolve();

    tunnel.onState(state => {
      if (state.type === 'ready' && this.readyState === SsmWebSocket.CONNECTING) {
        this.readyState = SsmWebSocket.OPEN;
        this._emit('open', new Event('open'));
      } else if (state.type === 'closed') {
        this._closed(1006, 'tunnel closed');
      }
    });
    tunnel.onData(bytes => {
      // Hand gloo-net an ArrayBuffer (binaryType === 'arraybuffer'). Slice to
      // the exact view in case the tunnel hands back a subarray.
      const buffer = bytes.buffer.slice(
        bytes.byteOffset,
        bytes.byteOffset + bytes.byteLength,
      );
      this._emit('message', new MessageEvent('message', { data: buffer }));
    });
    tunnel.onError(() => this._emit('error', new Event('error')));

    tunnel.open();
  }

  send(data) {
    // ironrdp-web (web-sys/gloo-net) hands us the RDCleanPath/RDP PDUs as a
    // Blob. Reading a Blob is async, so serialize conversion + write to keep the
    // byte stream strictly ordered. (Without the Blob branch this fell through
    // to String(data) and sent the literal text "[object Blob]".)
    this._sendQueue = this._sendQueue.then(async () => {
      const bytes = await this._toBytes(data);
      await this._tunnel.write(bytes);
    });
  }

  async _toBytes(data) {
    if (data instanceof Blob) return new Uint8Array(await data.arrayBuffer());
    if (data instanceof ArrayBuffer) return new Uint8Array(data);
    if (ArrayBuffer.isView(data)) {
      return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    }
    return new TextEncoder().encode(String(data));
  }

  close() {
    if (
      this.readyState === SsmWebSocket.CLOSING ||
      this.readyState === SsmWebSocket.CLOSED
    ) {
      return;
    }
    this.readyState = SsmWebSocket.CLOSING;
    this._tunnel.close();
    this._closed(1000, 'client closed');
  }

  _closed(code, reason) {
    if (this.readyState === SsmWebSocket.CLOSED) return;
    this.readyState = SsmWebSocket.CLOSED;
    this._emit(
      'close',
      new CloseEvent('close', { code, reason, wasClean: code === 1000 }),
    );
  }

  _emit(type, event) {
    const handler = this[`on${type}`];
    if (typeof handler === 'function') {
      handler.call(this, event);
    }
    this.dispatchEvent(event);
  }
}

/**
 * Scope-swap the global `WebSocket` so that constructing one with the sentinel
 * URL yields an {@link SsmWebSocket} bound to `tunnel`; every other URL falls
 * through to the native constructor (so Pusher, the SSM shell socket, etc. are
 * untouched). Returns a restore function — call it when the RDP session ends.
 *
 * Set the RDP client's `proxyAddress` to {@link SENTINEL_URL}.
 */
export function installSsmWebSocket(tunnel, sentinel = SENTINEL_URL) {
  const NativeWebSocket = window.WebSocket;

  function PatchedWebSocket(url, protocols) {
    if (url === sentinel) {
      return new SsmWebSocket(tunnel, url);
    }
    return new NativeWebSocket(url, protocols);
  }

  PatchedWebSocket.CONNECTING = NativeWebSocket.CONNECTING;
  PatchedWebSocket.OPEN = NativeWebSocket.OPEN;
  PatchedWebSocket.CLOSING = NativeWebSocket.CLOSING;
  PatchedWebSocket.CLOSED = NativeWebSocket.CLOSED;
  PatchedWebSocket.prototype = NativeWebSocket.prototype;

  window.WebSocket = PatchedWebSocket;
  return () => {
    window.WebSocket = NativeWebSocket;
  };
}

export { SENTINEL_URL };
